import React, { useEffect, useState } from "react";
import PageHeader from "../../components/common/PageHeader";
import StatsCardGroup from "../../components/common/StatsCardGroup";
import {
  CurrencyDollarIcon,
  ClockIcon,
  CalendarDaysIcon,
  CreditCardIcon,
} from "@heroicons/react/24/solid";
import Table from "../../components/common/Table";
import StripePayoutCard from "../../components/vendors/VendorStripe";
import DropdownSelect from "../../components/common/Dropdown";
import CustomToggleFilter from "../../components/common/CustomToggleFilter";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import {
  useVendorPayoutsSummary,
  useVendorPayoutsEarnings,
  useStripeStatus,
  useCreateStripeOnboardingLink,
  useStripeDashboardLink,
  useStripeUpdateLink,
} from "../../custom-hooks/queries";
import { formatDate } from "../../utils/HelperMethod";
import { toast } from "react-toastify";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";

const Payout = () => {
  // State for filters
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("earnings");
  const [earningStatus, setEarningStatus] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [payoutStatus, setPayoutStatus] = useState("");
  const [dateRange, setDateRange] = useState("");
  const pageSize = 10;

  // Calculate date range based on selection
  const getDateRange = (range) => {
    const today = new Date();
    let dateFrom = null;
    let dateTo = today.toISOString().split("T")[0];

    switch (range) {
      case "Today":
        dateFrom = dateTo;
        break;
      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        dateFrom = yesterday.toISOString().split("T")[0];
        dateTo = dateFrom;
        break;
      case "Last 7 Days":
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        dateFrom = last7.toISOString().split("T")[0];
        break;
      case "Last 30 Days":
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        dateFrom = last30.toISOString().split("T")[0];
        break;
      case "This Month":
        dateFrom = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        break;
      case "Last Month":
        const lastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        dateFrom = lastMonth.toISOString().split("T")[0];
        dateTo = new Date(today.getFullYear(), today.getMonth(), 0)
          .toISOString()
          .split("T")[0];
        break;
      default:
        dateFrom = null;
        dateTo = null;
    }
    return { dateFrom, dateTo };
  };

  const { dateFrom, dateTo } = getDateRange(dateRange);

  // Fetch Stripe status
  const { data: stripeData, isLoading: isStripeLoading } = useStripeStatus();
  const stripeStatus = stripeData?.data || stripeData || {};

  useEffect(() => {
    if (stripeStatus?.isConnected) {
      toast.success("Stripe account connected successfully");
    }
  }, []);

  // Stripe mutations
  const { mutate: createOnboardingLink, isPending: isCreatingOnboarding } =
    useCreateStripeOnboardingLink();
  const { mutate: getDashboardLink, isPending: isGettingDashboard } =
    useStripeDashboardLink();
  const { mutate: getUpdateLink, isPending: isGettingUpdate } =
    useStripeUpdateLink();

  // Fetch summary data
  const { data: summaryData, isLoading: isSummaryLoading } =
    useVendorPayoutsSummary();

  // Fetch earnings data
  const { data: earningsData, isLoading: isEarningsLoading } =
    useVendorPayoutsEarnings({
      page: currentPage,
      pageSize,
      tab: activeTab,
      dateFrom,
      dateTo,
      earningStatus: earningStatus || undefined,
    });

  // Stripe handlers
  const handleConnectStripe = () => {
    createOnboardingLink(undefined, {
      onSuccess: (response) => {
        const url = response?.data?.url || response?.url;
        if (url) {
          window.open(url, "_blank");
        } else {
          toast.error("Failed to get onboarding link");
        }
      },
      onError: () => {
        toast.error("Failed to create Stripe onboarding link");
      },
    });
  };

  const handleOpenDashboard = () => {
    // First check if we have dashboardUrl from status
    if (stripeStatus?.dashboardUrl) {
      window.open(stripeStatus.dashboardUrl, "_blank");
      return;
    }

    // Otherwise fetch it
    getDashboardLink(undefined, {
      onSuccess: (response) => {
        const url = response?.data?.url || response?.url;
        if (url) {
          window.open(url, "_blank");
        } else {
          toast.error("Failed to get dashboard link");
        }
      },
      onError: () => {
        toast.error("Failed to get Stripe dashboard link");
      },
    });
  };

  const handleUpdateAccount = () => {
    // First check if we have updateLinkUrl from status
    if (stripeStatus?.updateLinkUrl) {
      window.open(stripeStatus.updateLinkUrl, "_blank");
      return;
    }

    // Otherwise fetch it
    getUpdateLink(undefined, {
      onSuccess: (response) => {
        const url = response?.data?.url || response?.url;
        if (url) {
          window.open(url, "_blank");
        } else {
          toast.error("Failed to get update link");
        }
      },
      onError: () => {
        toast.error("Failed to get Stripe update link");
      },
    });
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "-";
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value}`;
  };

  // Stats data from API
  const stats = [
    {
      icon: (
        <div className="w-12 h-12 rounded-full bg-[#D9F6FB] flex items-center justify-center">
          <CurrencyDollarIcon className="h-6 w-6 text-[#00C3CC]" />
        </div>
      ),
      label: "Total Earning",
      value: `$${summaryData?.data?.totalEarnings}`,
    },
    {
      icon: (
        <div className="w-12 h-12 rounded-full bg-[#D9F6FB] flex items-center justify-center">
          <CalendarDaysIcon className="h-6 w-6 text-[#00C3CC]" />
        </div>
      ),
      label: "Processing Earnings",
      value: `$${summaryData?.data?.processingEarnings}`,
    },
    {
      icon: (
        <div className="w-12 h-12 rounded-full bg-[#D9F6FB] flex items-center justify-center">
          <CreditCardIcon className="h-6 w-6 text-[#00C3CC]" />
        </div>
      ),
      label: "Available Balance",
      value: `$${summaryData?.data?.availableBalance}`,
    },
    {
      icon: (
        <div className="w-12 h-12 rounded-full bg-[#D9F6FB] flex items-center justify-center">
          <ClockIcon className="h-6 w-6 text-[#00C3CC]" />
        </div>
      ),
      label: "Pending Earnings",
      value: formatCurrency(summaryData?.data?.pendingEarnings),
    },
  ];

  // Map API response to table format
  const payoutRows =
    earningsData?.data?.data?.map((item) => ({
      bookingId: item.bookingId,
      experience: item.experience,
      totalAmount: item.totalAmount,
      customer: item.customer,
      transactionStatus: item.transactionStatus,
      expectedPayoutDate: item?.expectedPayoutDate,
      earningStatus: item.earningStatus,
      bookingStatus: item.bookingStatus,
      netAmount: item?.netAmount,
    })) || [];

  const payoutOtherRows =
    earningsData?.data?.data?.map((item) => ({
      id: item.id,
      payoutId: item.payoutId,
      amount: item.amount,
      currency: item.currency?.toUpperCase(),
      status: item.status,
      arrivalDate: item.arrivalDate,
      createdAt: item.createdAt,
    })) || [];

  const pagination = {
    currentPage: earningsData?.data?.currentPage || currentPage,
    totalPages: earningsData?.data?.totalPages || 1,
    totalItems: earningsData?.data?.totalItems || 0,
  };

  const payoutColumns = [
    // {
    //   key: "bookingId",
    //   label: "BOOKING ID",
    //   width: "1fr",
    //   align: "left",
    // },
    {
      key: "experience",
      label: "EXPERIENCE",
      width: "1.3fr",
      align: "left",
    },
    {
      key: "amount",
      label: "TOTAL AMOUNT",
      width: "1fr",
      align: "center",
      render: (row) => `$${row.totalAmount}`,
    },
    {
      key: "customer",
      label: "CUSTOMER",
      width: "1.3fr",
      align: "center",
    },
    {
      key: "netAmount",
      label: "EXPECTED AMOUNT",
      width: "1.3fr",
      align: "center",
      render: (row) => `$${row.netAmount}`,
    },
    {
      key: "expectedPayoutDate",
      label: "EXPECTED PAYOUT DATE",
      width: "1.3fr",
      align: "center",
      render: (row) =>
        row?.expectedPayoutDate !== "-"
          ? formatDate(row?.expectedPayoutDate)
          : "-",
    },
    {
      key: "earningStatus",
      label: "EARNING STATUS",
      width: "1.3fr",
      align: "center",
      render: (row) => <StatusBadge status={row.earningStatus} />,
    },
  ];
  const payoutOtherColumn = [
    {
      key: "payoutId",
      label: "PAYOUT ID",
      width: "1.4fr",
      align: "left",
    },
    {
      key: "amount",
      label: "AMOUNT",
      width: "1fr",
      align: "center",
      render: (row) => `$${row.amount}`,
    },
    {
      key: "currency",
      label: "CURRENCY",
      width: "1fr",
      align: "center",
    },
    {
      key: "status",
      label: "STATUS",
      width: "1.2fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "arrivalDate",
      label: "ARRIVAL DATE",
      width: "1.4fr",
      align: "center",
      render: (row) => formatDate(row.arrivalDate),
    },
    {
      key: "createdAt",
      label: "CREATED AT",
      width: "1.4fr",
      align: "center",
      render: (row) => formatDate(row.createdAt),
    },
  ];

  // Handlers
  const handleTabChange = (value) => {
    const tabValue = value === "bar" ? "payouts" : "earnings";
    setActiveTab(tabValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setCurrentPage(1);
  };

  const handleEarningStatusChange = (value) => {
    setEarningStatus(value === "All" ? "" : value?.toLowerCase());
    setCurrentPage(1);
  };

  const handleBookingStatusChange = (value) => {
    setBookingStatus(value === "All" ? "" : value?.toLowerCase());
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const earningStatusOptions = [
    { id: "pending", name: "Pending" },
    { id: "processing", name: "Processing" },
    { id: "available", name: "Available" },
    { id: "canceled", name: "Canceled" },
  ];

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <div className="mb-4 sm:mb-5">
        <PageHeader
          title="Payouts"
          description="Manage your earnings and upcoming payouts"
        />
      </div>
      <div className="mt-4 sm:mt-6">
        {isStripeLoading ? (
          <div className="w-full bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-sm animate-pulse">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-full bg-gray-200"></div>
              <div className="flex-1">
                <div className="h-5 w-48 bg-gray-200 rounded"></div>
                <div className="h-4 w-64 bg-gray-200 rounded mt-2"></div>
              </div>
            </div>
          </div>
        ) : (
          <StripePayoutCard
            connected={stripeStatus?.isConnected || false}
            verifiedName={stripeStatus?.businessName || ""}
            accountStatus={
              stripeStatus?.verified ? "Verified" : "Pending Verification"
            }
            nextPayoutDate={
              stripeStatus?.nextPayoutDate
                ? formatDate(stripeStatus.nextPayoutDate)
                : "No payout scheduled yet"
            }
            stripeDetails={stripeStatus}
            payoutSchedule={stripeStatus?.payoutSchedule || "-"}
            onConnect={handleConnectStripe}
            onDashboard={handleOpenDashboard}
            onUpdate={handleUpdateAccount}
            isConnecting={isCreatingOnboarding}
            isDashboardLoading={isGettingDashboard}
            isUpdateLoading={isGettingUpdate}
          />
        )}
      </div>
      <div className="mt-4 sm:mt-6">
        <StatsCardGroup
          stats={stats}
          isLoading={isSummaryLoading}
          skeletonCount={4}
        />
      </div>
      <div
        className="
          bg-white 
          rounded-[12px]
          border border-[#D9D9D9]
          shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]
          overflow-hidden 
          mt-4 sm:mt-5"
      >
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-end gap-4 lg:gap-0 py-4 sm:py-6 px-4 sm:px-6">
          <div className="flex flex-col">
            <PageHeader title="Earnings By Bookings" margin={false} />
            <div className="mt-2">
              <CustomToggleFilter
                options={[
                  { label: "Payout", value: "bar" },
                  { label: "Earnings", value: "live" },
                ]}
                active={activeTab === "payouts" ? "bar" : "live"}
                onChange={handleTabChange}
              />
            </div>
          </div>
          {activeTab === "earnings" && (
            <div className="flex flex-col sm:flex-row justify-start sm:justify-end items-stretch sm:items-center gap-2 sm:gap-2 mt-2 lg:mt-2">
              <CustomFilterDropdown
                options={earningStatusOptions}
                onChange={handleEarningStatusChange}
                placeholder="Earning Status"
                valueKey="id"
                labelKey="name"
              />
            </div>
          )}
        </div>

        <Table
          columns={activeTab === "payouts" ? payoutOtherColumn : payoutColumns}
          data={activeTab === "payouts" ? payoutOtherRows : payoutRows}
          isLoading={isEarningsLoading}
          skeletonRows={pageSize}
        />
      </div>

      {!isEarningsLoading && pagination.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={pagination.currentPage || currentPage}
            totalPages={pagination.totalPages || 1}
            onPageChange={handlePageChange}
          />
        </div>
      )}
      <div className="mt-4 sm:mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-4 sm:p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Processing &amp; Payouts
        </h3>

        <ul className="space-y-3 text-sm text-gray-700 leading-[25px]">
          <li className="flex items-start gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00B8C8] mt-1"></span>
            <span>Toucan does not hold user funds at any stage.</span>
          </li>

          <li className="flex items-start gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00B8C8] mt-1"></span>
            <span>
              Payments and payouts are processed outside of the Toucan platform.
            </span>
          </li>

          <li className="flex items-start gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00B8C8] mt-1"></span>
            <span>
              Completed bookings become eligible for payout after booking
              confirmation.
            </span>
          </li>

          <li className="flex items-start gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00B8C8] mt-1"></span>
            <span>
              Refunds, disputes, or chargebacks may affect payout eligibility.
            </span>
          </li>

          <li className="flex items-start gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00B8C8] mt-1"></span>
            <span>
              Toucan does not manually initiate, approve, delay, or modify
              payouts.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Payout;
